import { is, List } from "immutable";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import * as redux from "redux";
import { ResumeAlertsForm } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { CareerCenterStatuses } from "../../../domain";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";
import { JobDetailsPartial } from "../../careerCenter";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
    dataLoading: state.careerCenter.loadingJobs,
    jobs: state.careerCenter.userJobs,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    goto: (url: string) => dispatch(push(url)),
    loadData: () => dispatch(Actions.CareerCenter.loadUserJobs()),
    notifyError: (message: string, icon?: string) => dispatch(Actions.Noti.notifyError(message, icon)),
    notifySuccess: (message: string, icon?: string) => dispatch(Actions.Noti.notifySuccess(message, icon)),
    removeJob: (job: M.CareerCenter.JobPost) => dispatch(Actions.CareerCenter.removeFromUserJobs(job)),
    toggleLoading: (loading: boolean) => dispatch(Actions.CareerCenter.toggleJobsLoading(loading)),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {
};

interface ConnectedState {
    api: Api;
    dataLoading: boolean;
    jobs: List<M.CareerCenter.JobPost>;
}

interface ConnectedDispatch {
    goto: (url: string) => void;
    loadData: () => void;
    notifyError: (message: string, icon?: string) => void;
    notifySuccess: (message: string, icon?: string) => void;
    removeJob: (job: M.CareerCenter.JobPost) => void;
    toggleLoading: (loading: boolean) => void;
}

interface OwnState {
    deleted: boolean;
    job?: M.CareerCenter.JobPost;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class JobDetailsComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private _confirmRenew: Comp.Modal.ConfirmModal | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
            deleted: false,
            loading: false,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        await this.props.loadData();
    }

    public componentWillMount() {
        if (!this.props.match.params.guid) {
            this.props.notifyError("Missing record identifier");
            this.props.goto(Paths.User.CareerCenter.Jobs);
        }

        // get job from state if available
        if (this.props.jobs.size)
            this.tryGetJobFromList(this.props.jobs);
        else
            this.props.toggleLoading(true);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public componentWillUpdate(nextProps: Readonly<CombinedProps>) {
        if (!is(this.props.jobs, nextProps.jobs)) {
            const job = this.tryGetJobFromList(nextProps.jobs);

            if (!job && !this.state.deleted) {
                this.props.notifyError(`Job record for identifier ${this.props.match.params.guid} could not be found.`);
                this.props.goto(Paths.User.CareerCenter.Jobs);
            }
        }
    }

    public render() {
        const title = this.props.jobs.size === 1
            ? "Your Job Post"
            : <Link to={Paths.User.CareerCenter.Jobs}>Your Job Posts</Link>;

        const details = this.state.job
            ? <Details
                api={this.props.api}
                job={this.state.job}
                showReturn={this.props.jobs.size > 1}
                onCopy={() => this.handleOnCopy(this.state.job)}
                onDelete={() => this.toggleDeleteConfirmation(this.state.job)}
                onEdit={() => this.handleOnEdit(this.state.job)}
                onRenew={() => this.toggleRenewConfirmation(this.state.job)}
                onReturn={() => this.props.goto(Paths.User.CareerCenter.Jobs)}
            />
            : null;

        return <Comp.Page.PublicPage style={{ position: "relative", minHeight: "200px" }} pageTitle={this.state.job ? this.state.job.title : "Job post details"}>
                <Comp.Generic.LoadingSpinner show={this.state.loading || this.props.dataLoading} />

                <Comp.Modal.ConfirmModal
                    headerClassName="warning"
                    onProceed={() => this.onDeleteConfirm()}
                    title="Confirm delete"
                    ref={(ref) => this._confirmDelete = ref}
                >
                    <p>Are you sure you want to delete this job post?</p>
                </Comp.Modal.ConfirmModal>

                <Comp.Modal.ConfirmModal
                    headerClassName="info"
                    labelProceed="Renew"
                    onProceed={() => this.onRenewConfirm()}
                    title="Confirm renewal"
                    ref={(ref) => this._confirmRenew = ref}
                >
                    <p>Please confirm you would like to renew this job post.</p>
                </Comp.Modal.ConfirmModal>

                <div id="content-primary">
                    <h1>
                        <Link to={Paths.User.Account}>Your Account</Link> &raquo; {title}
                    </h1>

                    {details}
                </div>

                <div id="content-secondary">
                    <ResumeAlertsForm />
                </div>
            </Comp.Page.PublicPage>;
    }

    private handleOnCopy(job?: M.CareerCenter.JobPost) {
        if (job)
            this.props.goto(Paths.CareerCenter.Job.Post + "?copy=" + job.guid);
    }

    private handleOnEdit(job?: M.CareerCenter.JobPost) {
        if (job && job.statusId === CareerCenterStatuses.Created.id)
            this.props.goto(Paths.CareerCenter.Job.Post + "/" + job.guid);
        else if (job)
            this.props.goto(Paths.User.CareerCenter.EditJob + "/" + job.guid);
    }

    private async onDeleteConfirm() {
        if (this._confirmDelete === null)
            return;

        const job = this._confirmDelete.getRef() as M.CareerCenter.JobPost;
        if (job === undefined) {
            this.props.notifyError("Job post is undefined in the confirmation modal.");
            return;
        }

        const qp = { queryParams: { guid: job.guid }};
        const result = await this.props.api.delete<boolean>(Api.endpoints.users.careerCenter.job, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess) {
            this.setState({ ...this.state, deleted: true }, () => {
                this.props.goto(Paths.User.CareerCenter.Jobs);
                this.props.notifySuccess(`Job post <strong>${job.title}</strong> has been successfully deleted.`);
                this.props.removeJob(job);
            });
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }

        if (this._confirmDelete !== null)
            this._confirmDelete.close();
    }

    private async onRenewConfirm() {
        if (this._confirmRenew === null)
            return;

        const job = this._confirmRenew.getRef() as M.CareerCenter.JobPost;
        if (job === undefined) {
            this.props.notifyError("Job post is undefined in the confirmation modal.");
            return;
        }

        const qp = { queryParams: { guid: job.guid }};
        const result = await this.props.api.post<M.CareerCenter.JobPost>(Api.endpoints.users.careerCenter.renewJob, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess) {
            await this.props.loadData();
            this.props.notifySuccess(`Job post <strong>${job.title}</strong> has been successfully renewed.`);
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }

        if (this._confirmRenew !== null)
            this._confirmRenew.close();
    }

    private toggleDeleteConfirmation(job?: M.CareerCenter.JobPost) {
        if (job && this._confirmDelete !== null) {
            this._confirmDelete.show(job);
        }
    }

    private toggleRenewConfirmation(job?: M.CareerCenter.JobPost) {
        if (job && this._confirmRenew !== null) {
            this._confirmRenew.show(job);
        }
    }

    private tryGetJobFromList(list: List<M.CareerCenter.JobPost>): M.CareerCenter.JobPost | undefined {
        const job = list.find((x) => x !== undefined && x.guid.toLowerCase() === this.props.match.params.guid.toLowerCase());

        if (job) {
            this.setState({ ...this.state, job });
        }

        return job;
    }
}

export const JobDetails: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(JobDetailsComponent) as any);

interface DetailsProps {
    api: Api;
    job: M.CareerCenter.JobPost;
    onCopy: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onRenew: () => void;
    onReturn: () => void;
    showReturn: boolean;
}

const Details: React.SFC<DetailsProps> = (props) => {
    const now = new Date();
    const job = props.job;
    let expiresOn: Date | undefined;
    let renewBtn = null;

    if (job.expiresOn) {
        expiresOn = Utility.utcToLocal(job.expiresOn);
        if (expiresOn && now > expiresOn)
            renewBtn = <BS.Button bsStyle="link" onClick={() => props.onRenew()}><strong>Renew</strong></BS.Button>;
    }

    const returnBtn = props.showReturn
        ? <BS.Button bsStyle="link" onClick={() => props.onReturn()}>&laquo; Return</BS.Button>
        : null;

    const buttons = returnBtn !== null || renewBtn !== null
        ? <BS.ButtonGroup>
            {returnBtn}
            {renewBtn}
        </BS.ButtonGroup>
        : null;

    return <Comp.Generic.WrapperLess>
        <div className="btn-toolbar">
            <BS.ButtonGroup>
                <Link className="btn btn-primary" to={Paths.CareerCenter.Job.Post}><Comp.Generic.Icon icon="plus" /> Create new post</Link>
            </BS.ButtonGroup>

            <Comp.Tools.CrudTools
                entity="job post"
                includeCopy={job.statusId > CareerCenterStatuses.Created.id}
                onCopy={() => props.onCopy()}
                onDelete={() => props.onDelete()}
                onEdit={() => props.onEdit()}
            >
                {buttons}
            </Comp.Tools.CrudTools>
        </div>

        <JobDetailsPartial job={props.job} showExpires />
    </Comp.Generic.WrapperLess>;
};



// WEBPACK FOOTER //
// ./src/containers/user/careerCenter/JobDetails.tsx